import { useScrollReveal } from '@/hooks/useScrollReveal';

const VIDEO_URL = 'https://storage.helloreaddy.io/project_files/6cdd528b-b3f6-427c-a8db-1dc5e6497a05/34c40b30-8fcc-47fe-9eb8-f5fc6c2cf2ee_Water_pours_from_pipe_into_202607302220.mp4';

const LOGO_URL = 'https://storage.helloreaddy.io/project_files/6cdd528b-b3f6-427c-a8db-1dc5e6497a05/2e970e52-bbc6-4708-9dc9-30721acea5db_compressed_watermarked_img_8696222614542899108.webp';

export default function Hero() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  const handleScrollTo = (id: string) => {
    const target = document.querySelector(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="inicio"
      className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden"
    >
      {/* Background video */}
      <div className="absolute inset-0">
        <video
          src={VIDEO_URL}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        />
        {/* Overlay gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-900/60 to-primary-950/80" />
        {/* Subtle texture */}
        <div className="absolute inset-0 texture-dots opacity-30" />
      </div>

      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] left-[10%] w-3 h-3 rounded-full bg-accent-400/30 animate-float" />
        <div
          className="absolute top-[35%] right-[15%] w-2 h-2 rounded-full bg-accent-300/40 animate-float"
          style={{ animationDelay: '1.2s' }}
        />
        <div
          className="absolute bottom-[30%] left-[20%] w-4 h-4 rounded-full bg-white/10 animate-float"
          style={{ animationDelay: '2.1s' }}
        />
        <div
          className="absolute top-[60%] right-[25%] w-2 h-2 rounded-full bg-accent-500/25 animate-float"
          style={{ animationDelay: '0.7s' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-20">
        <div
          ref={titleRef}
          className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Logo in hero */}
          <div className="mb-6 md:mb-8 flex justify-center">
            <div className="w-20 h-20 md:w-28 md:h-28 bg-white/10 backdrop-blur-sm rounded-2xl p-3 flex items-center justify-center animate-float">
              <img
                src={LOGO_URL}
                alt="Acualys-Bo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight">
            Acualys-Bo
          </h1>

          {/* Tagline */}
          <p className="mt-4 md:mt-6 text-lg sm:text-xl md:text-2xl text-accent-200 font-medium max-w-2xl mx-auto">
            Control total de tu cooperativa de agua
          </p>

          {/* Description */}
          <p className="mt-4 md:mt-5 text-sm sm:text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            Sistema integral de gestión para EPAS de agua en Bolivia.
            Lecturas, registro, cortes, finanzas, portal online.
          </p>

          {/* CTAs */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => handleScrollTo('#contacto')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-accent-500 hover:bg-accent-600 text-white text-base font-semibold transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line" />
              Solicitar demo
            </button>
            <button
              onClick={() => handleScrollTo('#modulos')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white/10 hover:bg-white/20 text-white text-base font-medium transition-all border border-white/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apps-line" />
              Ver módulos
            </button>
          </div>

          {/* Stats mini */}
          <div className="mt-12 md:mt-16 flex items-center justify-center gap-6 md:gap-10 text-white/60 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-accent-400" />
              <span>10+ años de experiencia</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/20" />
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line text-accent-400" />
              <span>Bolivia</span>
            </div>
          </div>
        </div>
      </div>

      {/* Wave divider at bottom */}
      <div className="wave-divider">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.1,118.92,156.86,95.73,321.39,56.44Z"
            fill="oklch(var(--background-50))"
          />
        </svg>
      </div>
    </section>
  );
}